import React, { useState, useEffect } from 'react';
import MenuBar from './common/MenuBar';
import UnlockUserModal from './common/UnlockUserModal';
import { DataGrid } from '@mui/x-data-grid';
import toast, { Toaster } from 'react-hot-toast';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { loadLockedItems, lockProcess } from '../Services/loadOrder';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const UnlockOrders = () => {
  const [showUnlockUserModal, setShowUnlockUserModal] = useState(false);
  const [taskId, setTaskId] = useState(0);
  const user = JSON.parse(localStorage.getItem('user'));
  const pharmacyId = user?.pharmacyId;
  const userId = user?.userId;

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]); // keep original for search reset
  const [currentParams, setCurrentParams] = useState({});
  const [loading, setLoading] = useState(false);

  // search states
  const [selectedField, setSelectedField] = useState('');
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const data = await loadLockedItems(pharmacyId, taskId);

      const filteredRows = data.map((item, index) => ({
        id: index + 1,
        authNo: item.AuthNo,
        lastName: item.LastName,
        firstName: item.FirstName,
        taskName: item.TaskName,
        userName: item.UserName,
        contract: item.Contract,
        lockedStatus: item.LockedStatus,
        taskId: item.TaskID,
        memberId: item.MemberID,
      }));
      setRows(filteredRows);
      setAllRows(filteredRows); // store original
    };
    fetchData();
  }, [pharmacyId, taskId]);

  const handleConfirm = async () => {
    setShowUnlockUserModal(false);
    setLoading(true);

    const response = await lockProcess(
      currentParams.contract,
      userId,
      currentParams.taskId,
      pharmacyId,
      'UnLock'
    );

    if (response?.Status === 'Unlocked') {
      setLoading(false);
      toast.success('Order unlocked successfully.');
      window.location.reload();
    } else {
      toast.error('Failed to unlock the order. Please try again.');
    }
  };

  // FRONTEND SEARCH
  const handleSearch = () => {
    setSearchLoading(true);

    // empty text means show all
    if (searchText.trim() === '') {
      setRows(allRows);
      setSearchLoading(false);
      return;
    }

    const q = searchText.trim().toLowerCase();

    // if no specific field chosen, search in few common fields
    if (!selectedField) {
      const filtered = allRows.filter((r) => {
        return (
          (r.authNo && r.authNo.toString().toLowerCase().includes(q)) ||
          (r.memberId && r.memberId.toString().toLowerCase().includes(q)) ||
          (r.lastName && r.lastName.toLowerCase().includes(q)) ||
          (r.firstName && r.firstName.toLowerCase().includes(q)) ||
          (r.taskName && r.taskName.toLowerCase().includes(q)) ||
          (r.userName && r.userName.toLowerCase().includes(q)) ||
          (r.contract && r.contract.toLowerCase().includes(q)) ||
          (r.lockedStatus && r.lockedStatus.toLowerCase().includes(q))
        );
      });
      setRows(filtered);
      setSearchLoading(false);
      return;
    }

    // field-specific
    const filtered = allRows.filter((r) => {
      switch (selectedField) {
        case 'AuthNo':
          return r.authNo?.toString().toLowerCase().includes(q);
        case 'MemberID':
          return r.memberId?.toString().toLowerCase().includes(q);
        case 'LastName':
          return r.lastName?.toLowerCase().includes(q);
        case 'FirstName':
          return r.firstName?.toLowerCase().includes(q);
        case 'TaskName':
          return r.taskName?.toLowerCase().includes(q);
        case 'UserName':
          return r.userName?.toLowerCase().includes(q);
        case 'Contract':
          return r.contract?.toLowerCase().includes(q);
        case 'LockedStatus':
          return r.lockedStatus?.toLowerCase().includes(q);
        default:
          return true;
      }
    });

    setRows(filtered);
    setSearchLoading(false);
  };

  const handleViewAll = () => {
    setSearchText('');
    setSelectedField('');
    setRows(allRows);
  };

  const columns = [
    { field: 'authNo', headerName: 'Auth No', flex: 1, headerClassName: 'pink-header' },
    { field: 'memberId', headerName: 'Member Id', flex: 1, headerClassName: 'pink-header' },
    { field: 'lastName', headerName: 'Last Name', flex: 1, headerClassName: 'pink-header' },
    { field: 'firstName', headerName: 'First Name', flex: 1, headerClassName: 'pink-header' },
    { field: 'taskName', headerName: 'Task Name', flex: 1, headerClassName: 'pink-header' },
    { field: 'userName', headerName: 'User Name', flex: 1, headerClassName: 'pink-header' },
    {
      field: 'contract',
      headerName: 'Contract',
      flex: 1,
      headerClassName: 'pink-header',
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            lineHeight: 1.3,
            textAlign: 'left',
            padding: '2px 4px',
            width: '100%',
            height: '100%',
          }}
        >
          {params.value}
        </div>
      ),
    },
    { field: 'lockedStatus', headerName: 'Locked Status', flex: 1, headerClassName: 'pink-header' },
    {
      field: 'unlock',
      headerName: 'Unlock Task',
      flex: 1,
      headerClassName: 'pink-header',
      renderCell: (params) => (
        <strong>
          <button
            onClick={() => {
              setShowUnlockUserModal(true);
              setCurrentParams(params.row);
            }}
            style={{
              background: '#ec008c',
              color: 'white',
              padding: '3px 6px',
              fontWeight: 'bold',
              border: '1px solid #fff',
              borderRadius: '5px',
            }}
          >
            Unlock
          </button>
        </strong>
      ),
    },
  ];

  return (
    <div>
      <MenuBar />
      <Toaster position="top-right" />
      <div className="top-page-option">
        <div className="top-page-option-name" style={{ display: 'flex', alignItems: 'center' }}>
          <a
            href="#/home"
            style={{
              textDecoration: 'none',
              color: '#ec088c',
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <ChevronLeft color="#ec088c" size={18} />
            <span style={{ marginLeft: '5px' }}>Back</span>
          </a>
        </div>
        <div className="top-page-option-name" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <span>Admin</span>
          <ChevronRight color="#ec088c" size={18} style={{ position: 'relative', top: '2px' }} />
          <span>Unlock Orders</span>
        </div>
      </div>

      {/* SEARCH BAR */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'right',
          alignItems: 'center',
          gap: '8px',
          marginLeft: 'auto',
          marginRight: '6.2%',
          marginTop: '12px',
          marginBottom: '4px',
        }}
      >
        <FormControl
          size="small"
          sx={{ minWidth: 140, height: '30px', background: '#fff', borderRadius: '4px', marginRight: '10px' }}
        >
          <InputLabel
            sx={{
              fontFamily: 'Segoe ui',
              background: '#fff',
              color: 'black',
              fontSize: '12px',
            }}
          >
            --Select--
          </InputLabel>
          <Select
            value={selectedField}
            label="--Select--"
            onChange={(e) => setSelectedField(e.target.value)}
            sx={{
              fontFamily: 'Segoe ui',
              fontSize: '12px',
              border: '1px solid rgb(236, 151, 202)',
              borderRadius: '15px',
              height: '30px',
              boxShadow: 'none',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ec008c',
                boxShadow: '0 0 6px rgba(236, 0, 140, 0.4)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ec008c',
              },
            }}
          >
            <MenuItem value="" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              --Select--
            </MenuItem>
            <MenuItem value="AuthNo" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              Auth No
            </MenuItem>
            <MenuItem value="MemberID" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              Member ID
            </MenuItem>
            <MenuItem value="LastName" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              Last Name
            </MenuItem>
            <MenuItem value="FirstName" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              First Name
            </MenuItem>
            <MenuItem value="TaskName" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              Task Name
            </MenuItem>
            <MenuItem value="UserName" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              User Name
            </MenuItem>
            <MenuItem value="Contract" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              Contract
            </MenuItem>
            <MenuItem value="LockedStatus" sx={{ fontSize: '12px', '&:hover': { backgroundColor: '#f5f5f5' } }}>
              Locked Status
            </MenuItem>
          </Select>
        </FormControl>

        <input
          type="text"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearch();
          }}
          placeholder="Type to search..."
          style={{
            padding: '5px 5px',
            borderRadius: '15px',
            border: '1px solid rgb(236, 151, 202)',
            outline: 'none',
            boxShadow: '0 1px 2px rgb(236, 151, 202)',
            transition: 'all 0.3s ease',
            minWidth: '180px',
          }}
          onFocus={(e) => {
            e.target.style.border = '1px solid #ec008c';
            e.target.style.boxShadow = '0 0 6px rgba(236, 0, 140, 0.4)';
          }}
          onBlur={(e) => {
            e.target.style.border = '1px solid rgb(236, 151, 202)';
            e.target.style.boxShadow = '0 1px 2px rgb(236, 151, 202)';
          }}
        />

        <button
          onClick={handleSearch}
          disabled={searchLoading}
          style={{
            padding: '6px 18px',
            borderRadius: '4px',
            background: '#ec008c',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Search
        </button>
        <button
          onClick={handleViewAll}
          style={{
            padding: '5px 18px',
            borderRadius: '4px',
            background: '#ec008c',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          View All
        </button>
      </div>

      <div style={{ height: 'auto', width: '87%', margin: 'auto' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5]}
          hideFooter
          sx={{
            fontSize: '12px',
            fontFamily: 'Segoe UI',
            '& .pink-header': {
              color: '#fff',
              backgroundColor: '#ec008c',
              fontWeight: 'bold',
              whiteSpace: 'normal !important',
              wordBreak: 'break-word !important',
              overflowWrap: 'break-word !important',
              lineHeight: '1.3 !important',
              textAlign: 'center',
              padding: '8px 2px !important',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: '#f5f5f5 !important',
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
              boxShadow: 'none !important',
              backgroundColor: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
          }}
        />
      </div>

      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              border: '6px solid #f3f3f3',
              borderTop: '6px solid #EC088C',
              borderRadius: '50%',
              width: 48,
              height: 48,
              animation: 'spin 1s linear infinite',
            }}
          />
          <div style={{ marginTop: 24, fontSize: '1.2rem', fontWeight: 500, color: '#222' }}>
            Your request is being processed
          </div>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% {transform: rotate(360deg);} }`}</style>
        </div>
      )}

      <UnlockUserModal
        open={showUnlockUserModal}
        onClose={() => setShowUnlockUserModal(false)}
        taskId={taskId}
        initialParams={currentParams}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default UnlockOrders;
